/**
 * How a queued Claude, or a worktree's queued prompt, starts: Manual, Ready,
 * Under or Wastage (shared/startMode.ts), picked as one from
 * `StartModeSelect.vue` and saved with `PUT /api/tmux/queue/:id/start-mode`
 * (a prompt: `PATCH /api/tmux/prompts/:id` with `startMode`).
 */
import type { StartMode } from "@shared/startMode.js";

export type { StartMode };

export const START_MODES: readonly { value: StartMode; label: string }[] = [
  { value: "manual", label: "Manual" },
  { value: "ready", label: "Ready" },
  { value: "under", label: "Under" },
  { value: "wastage", label: "Wastage" },
];

export const startModeLabel = (mode: StartMode): string => START_MODES.find((m) => m.value === mode)!.label;

/** What Under and Wastage mean, for every place that offers them. */
export const UNDER_TITLE =
  "Under: sent by itself while Claude's weekly usage is under the week's pace (less of the allowance used than of the week gone) and the 5-hour window is within half its pace — or once allowance is being wasted";
export const WASTAGE_TITLE =
  "Wastage: sent by itself only once fewer hours are left in the week than even flat-out use would need to spend what is left of the allowance — work worth running only on tokens that would otherwise be lost";

/** What each mode does to a worktree's queued prompt (the pill's Queue prompt,
 *  the Chat's prompt box). */
export const PROMPT_MODE_TITLES: Record<StartMode, string> = {
  manual: "Manual: it waits in the queue until another start is picked",
  ready: "Ready: send it once nothing it waits on holds it, while the Global settings' Parallel Claudes has room and the Claude here is not mid-turn",
  under: UNDER_TITLE,
  wastage: WASTAGE_TITLE,
};

/** What each mode does to a quick task's queued row (the git panel's quick add,
 *  the repo switcher's "+"). */
export const QUICK_MODE_TITLES: Record<StartMode, string> = {
  manual: "Manual: queued, and started only by its Send",
  ready: "Ready: Claude starts on it by itself once nothing holds it — in a worktree named after the title",
  under: `${UNDER_TITLE} — as you, in a worktree named after the title`,
  wastage: `${WASTAGE_TITLE} — as you, in a worktree named after the title`,
};

/** What each mode does to a repo's auto-fix (the Repos view, the git panel's ⚙ options). */
/** A repo's start limit (`RepoSettings.startLimit`): never Manual. */
export const START_LIMIT_TITLES: Record<StartMode, string> = {
  manual: "",
  ready: "Ready: no limit — each queued task and prompt goes by its own start mode, and auto-fix at once",
  under: `${UNDER_TITLE}. Every queued task and prompt in the repo, and auto-fix, goes no sooner, whatever its own start mode`,
  wastage: `${WASTAGE_TITLE}. Every queued task and prompt in the repo, and auto-fix, goes no sooner, whatever its own start mode`,
};
